import { clinicalLimitStatus, formatStructuredClinicalLimit } from './clinicalTargetSemantics';
import React from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { clampPercent, colors, formatMetric, type AnalysisData, type KpiItem } from './analysis.shared';
import type { RowTone } from './NutritionReportSectionTable';
import {
  getSuggestedRdaIntakeStatusFromTarget,
  getSuggestedRdaMinimumTargetIntakeStatus,
  getSuggestedRdaTrendIconKind,
  suggestedRdaToneStyles,
} from './suggestedRdaTone';

type TopKpiBucket = 'energy' | 'protein' | 'carbs' | 'fat' | 'fibre' | 'water';

type StripKpiRow = KpiItem & { rowKey: TopKpiBucket };

/** Same numbers as `MacronutrientAnalysisSection` / merge output — avoids stale `kpis` rows. */
function buildTopStripRows(data: AnalysisData): StripKpiRow[] {
  const spec: Array<Pick<StripKpiRow, 'rowKey' | 'label' | 'value' | 'unit' | 'target'>> = [
    { rowKey: 'energy', label: 'Energy', value: data.energy, unit: 'kcal', target: data.energyTarget },
    { rowKey: 'protein', label: 'Protein', value: data.protein, unit: 'g', target: data.proteinTarget },
    { rowKey: 'carbs', label: 'Carbs', value: data.carbs, unit: 'g', target: data.carbsTarget },
    { rowKey: 'fat', label: 'Total Fat', value: data.fat, unit: 'g', target: data.fatTarget },
    { rowKey: 'fibre', label: 'Fibre', value: data.fibre, unit: 'g', target: data.fibreTarget },
    { rowKey: 'water', label: 'Water', value: data.water, unit: 'L', target: data.waterTarget },
  ];
  return spec.map((row) => ({ ...row }));
}

/** Figma 97:5687 / 97:5909 — macro tab KPI strip (presentation only). */
export type AnalysisKpiGridAppearance = 'default' | 'macroFigma';

interface AnalysisKpiGridProps {
  analysisData: AnalysisData;
  appearance?: AnalysisKpiGridAppearance;
}

function getTrendIcon(tone: RowTone): React.ReactNode {
  const color = suggestedRdaToneStyles[tone].bar;
  const iconKind = getSuggestedRdaTrendIconKind(tone);

  if (iconKind === 'down') {
    return <TrendingDownRoundedIcon sx={{ fontSize: 14, color }} />;
  }
  if (iconKind === 'up') {
    return <TrendingUpRoundedIcon sx={{ fontSize: 14, color }} />;
  }
  return <TrendingFlatRoundedIcon sx={{ fontSize: 14, color }} />;
}

function resolveKpiIntakeStatus(item: StripKpiRow): { color: string; label: string; tone: RowTone } {
  if (item.rowKey === 'fibre') {
    const rawProgressPercent = Math.max(0, (item.value / Math.max(1, item.target)) * 100);
    return getSuggestedRdaMinimumTargetIntakeStatus(rawProgressPercent);
  }

  return getSuggestedRdaIntakeStatusFromTarget(item.value, item.target);
}

export const AnalysisKpiGrid: React.FC<AnalysisKpiGridProps> = ({ analysisData, appearance = 'default' }) => {
  const isMacroFigma = appearance === 'macroFigma';
  const visibleKpis = React.useMemo(() => buildTopStripRows(analysisData), [analysisData]);

  return (
    <Box
      sx={{
        mt: isMacroFigma ? 0 : 1.5,
        display: 'grid',
        gap: isMacroFigma ? '12px' : 1.25,
        gridTemplateColumns: isMacroFigma
          ? 'repeat(auto-fit, minmax(min(198px, 100%), 1fr))'
          : 'repeat(auto-fit, minmax(min(196px, 100%), 1fr))',
      }}
    >
      {visibleKpis.map((item) => {
        const rawProgressPercent = Math.max(0, (item.value / Math.max(1, item.target)) * 100);
        const progressPercent = clampPercent(rawProgressPercent);
        const clinicalLimit = analysisData.mealPlanClinicalLimits?.[item.rowKey as keyof NonNullable<AnalysisData['mealPlanClinicalLimits']>];
        const clinicalStatus = clinicalLimit ? clinicalLimitStatus({ limit: clinicalLimit, intake: item.value }) : null;
        const status = clinicalStatus ? { tone: clinicalStatus.tone, color: suggestedRdaToneStyles[clinicalStatus.tone].bar } : resolveKpiIntakeStatus(item);

        return (
          <Paper
            key={item.rowKey}
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #f3f4f6',
              boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.1)',
              p: 2,
              bgcolor: isMacroFigma ? '#ffffff' : colors.cardBackground,
              minWidth: 0,
              minHeight: isMacroFigma ? 115.5 : 114,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              rowGap: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: isMacroFigma ? '1.1172px' : '0.112em',
                  color: '#99a1af',
                }}
              >
                {item.label}
              </Typography>
              {getTrendIcon(status.tone)}
            </Box>

            <Typography
              sx={{
                mt: 0.25,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '18px',
                letterSpacing: isMacroFigma ? '-0.15px' : '-0.024em',
                color: '#1e2939',
                overflowWrap: 'anywhere',
              }}
            >
              {formatMetric(item.value, item.unit === 'L' ? 1 : 0)}
              <Typography component="span" sx={{ fontSize: '12px', lineHeight: '16px', color: '#99a1af', ml: 0.5, fontWeight: 400 }}>
                {item.unit}
              </Typography>
            </Typography>

            <LinearProgress
              variant="determinate"
              value={Math.min(100, progressPercent)}
              sx={{
                mt: 0.25,
                height: 4,
                borderRadius: 999,
                bgcolor: '#f3f4f6',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: status.color,
                },
              }}
            />

            <Typography
              sx={{
                mt: 0.1,
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                lineHeight: '13.5px',
                letterSpacing: '0.018em',
                color: '#99a1af',
                overflowWrap: 'anywhere',
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>{formatMetric(rawProgressPercent, 0)}%</Box>
              {' · '}
              {item.rowKey === 'fat' ? 'Planning target' : 'Target'} {item.rowKey === 'fibre' ? '≥' : ''}{formatMetric(item.target, item.unit === 'L' ? 1 : 0)} {item.unit}
              {clinicalLimit && <Box component="span" sx={{ display: 'block' }}>Clinical limit: {formatStructuredClinicalLimit(clinicalLimit)}</Box>}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};
